/** Copyright 2025 SAP SE or an SAP affiliate company. All rights reserved. */
import React, { useEffect, useRef, useState } from 'react';
import type { FileUploaderChangeEventDetail } from '@ui5/webcomponents/dist/FileUploader';
import type { Ui5CustomEvent, FileUploaderDomRef, MenuDomRef } from '@ui5/webcomponents-react';
import { actions } from '@/store/Products';
import {
    Label,
    Table,
    TableCell,
    TableRow,
    Card,
    CardHeader,
    Bar,
    Button,
    Dialog,
    TabContainer,
    Tab,
    Text,
    ComboBoxItem,
    ComboBox,
    Input,
    Title,
    FileUploader,
    RadioButton,
    Modals,
    Menu,
    MenuItem,
    TableHeaderRow,
    TableHeaderCell,
    FlexBox,
    MessageBoxType,
    MessageBox,
    TableGrowing,
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/upload.js';
import '@ui5/webcomponents-icons/dist/action-settings.js';
import '@ui5/webcomponents-icons/dist/upload-to-cloud.js';
import '@ui5/webcomponents-fiori/dist/FilterItem.js';
import '@ui5/webcomponents-icons/dist/download.js';
import { AxiosHeaders } from 'axios';
import { MenuItemClickEventDetail } from '@ui5/webcomponents/dist/Menu';
import useWindowSize from '@/hooks/useWindowSize';
import { useTranslations } from '@/hooks/useTranslations';
import { createStyles } from '@/utils/styles';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import './TableLayout.css';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { loadingActions } from '@/store/loadingSlice';
import axiosInstance from '@/utils/axiosInstance';

export enum SortingOrder {
    Ascending = 'ASC',
    Descending = 'DESC',
}

export enum SortingBy {
    Domain = 'domain',
    Name = 'name',
}

const TableLayout: React.FC<any> = ({ content }) => {
    const { height } = useWindowSize();
    const [, setSelectedFile] = useState<any>(null);
    const fileUploaderRef = useRef<any>(null);
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [doLoadMoreData, setDoLoadMoreData] = useState<boolean>(true);
    const [runTableDataPresent, setRunTableDataPresent] = useState<boolean>(false);
    const [data, setData] = useState<any>([]);
    const [commodityCodeValue, setCommodityCodeValue] = useState<string>();
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [page, setPage] = useState<number>(0);
    const [filterParams, setFilterParams] = useState<any>([]);
    const [, setFilterParamsForUnspsc] = useState<any>([]);
    const [lastSelectedItemForFilter, setLastSelectedItemForFilter] = useState<string>('');
    const filterComboxBoxRefList = useRef<any[]>([]);
    const filterInputRefList = useRef<any[]>([]);
    const productList = useAppSelector(state => state.productList);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [filterCriteria, setFilterCriteria] = useState<any[]>([
        {
            column: 'runId',
            value: productList.currentRunId,
        },
        {
            column: 'deleted',
            value: false,
        },
    ]);
    const [sortCriteria, setSortCriteria] = useState<any[]>([
        {
            propertyName: 'enabled',
            sortOrder: 'DESC',
        },
    ]);

    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [uploadFlag, setUploadFlag] = useState(false);
    const tableHeight = Math.round(1.0 * height - 435);
    const { t } = useTranslations();
    const errorBox = () => {
        Modals.showMessageBox({
            type: 'Error',
            style: { width: '35%' },
            resizable: true,
            children: <Text>{t('COMMODITY_CODE_ERROR_MSG')}</Text>,
        });
    };

    const [filterColumns, setFilterColumns] = useState<any>([]);
    const [order, setOrder] = useState<string>('');

    const onFileChange = (event: Ui5CustomEvent<FileUploaderDomRef, FileUploaderChangeEventDetail>) => {
        const file = event.detail?.files;
        if (file) {
            setSelectedFile(file);
            if (fileUploaderRef.current) {
                fileUploaderRef.current.value = '';
            }
        }
        handleUpload(event.detail?.files);
    };

    const handleUpload = (file: FileList | null) => {
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        if (!file || file.length === 0) {
            return;
        }
        const selectFile = file[0];
        if (selectFile.size >= MAX_FILE_SIZE) {
            setErrorMessage(t('FILE_UPLOAD_SIZE_EXCEED'));
            setOpenErrorDialog(true);
            return;
        }
        const formData = new FormData();
        formData.append('file', selectFile);
        if (!runTableDataPresent) {
            uploadPostCall(formData);
        } else {
            uploadPostCall(formData, true);
        }
    };

    const uploadPostCall = (formData: FormData, reupload: boolean = false) => {
        dispatch(loadingActions.startLoading());

        const headers = new AxiosHeaders();
        headers.set('Content-Type', 'multipart/form-data');
        headers.set('reupload', reupload);
        headers.set('commodityCode', commodityCodeValue);
        axiosInstance
            .post(`/commodity-codes-run/upload/${productList.currentRunId}`, formData, {
                headers: headers,
                timeout: 900 * 10000,
            })
            .then((response: any) => {
                if (response.data.errorneous) {
                    errorBox();
                }
            })
            .then(() => {
                setPage(1);
                setDoLoadMoreData(true);
                dispatch(actions.setCsvUploaded(true));
                console.log('Upload');
                const headers = new AxiosHeaders();
                headers.set('CommodityCode', commodityCodeValue);
                axiosInstance
                    .post(
                        `/commodity-codes-run/`,
                        {
                            filterCriteria: filterCriteria,
                            sortCriteria: sortCriteria,
                        },
                        { headers: headers }
                    )
                    .then((response: any) => {
                        if (response.data.content.length > 0) {
                            // check for if there is any data in ccRunTable
                            setRunTableDataPresent(true);
                        }
                        return response.data.content;
                    })
                    .then(data =>
                        data.map((dataObj: any) => {
                            return {
                                ...dataObj,
                                key: dataObj.uniqueKey,
                            };
                        })
                    )
                    .then(response => setData(response))
                    .catch(error => {
                        console.log(error);
                    });
            })
            .catch(error => {
                if (error.response.status === 413) {
                    setErrorMessage(t('FILE_UPLOAD_SIZE_EXCEED'));
                } else if (error.response.status === 400 || error.response.status === 422) {
                    setErrorMessage(error.response.data.message);
                }
                setOpenErrorDialog(true);
            })
            .finally(() => dispatch(loadingActions.stopLoading()));
    };

    useEffect(() => {
        (async () => {
            dispatch(loadingActions.startLoading());
            const configKeyPromise: any = await axiosInstance.get(
                `product-configurations/configuration-key/Application.ClassificationCodes.SystemCommodityCodeDomainName/product-runs/${productList.currentRunId}`
            );
            const tempCommodityCodeValue = configKeyPromise.data.configurationValue || content.defaultValue;
            setCommodityCodeValue(tempCommodityCodeValue);
            const headers = new AxiosHeaders();
            headers.set('CommodityCode', tempCommodityCodeValue);
            const ccRunDataPromise: any = axiosInstance.post(
                `/commodity-codes-run/`,
                {
                    filterCriteria: filterCriteria,
                    sortCriteria: sortCriteria,
                },
                {
                    headers: headers,
                }
            );
            ccRunDataPromise
                .then((response: any) => {
                    if (response.data && response.data.content.length > 0) {
                        // check for if there is any data in ccRunTable
                        setRunTableDataPresent(true);
                        setUploadFlag(true);
                    }
                })
                .finally(() => {
                    dispatch(loadingActions.stopLoading());
                });
        })();
    }, []);

    useEffect(() => {
        if (productList.currentStep.name === 'Enablement') {
            if (runTableDataPresent) {
                dispatch(loadingActions.startLoading());
                const headers = new AxiosHeaders();
                headers.set('CommodityCode', commodityCodeValue);
                axiosInstance
                    .post(
                        `/commodity-codes-run/`,
                        {
                            filterCriteria: filterCriteria,
                            sortCriteria: sortCriteria,
                        },
                        {
                            headers: headers,
                        }
                    )
                    .then((response: any) => response.data.content)
                    .then(data =>
                        data.map((dataObj: any) => {
                            return {
                                ...dataObj,
                                key: dataObj.uniqueKey,
                            };
                        })
                    )
                    .then(response => {
                        setData(response);
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .finally(() => dispatch(loadingActions.stopLoading()));
            }
        }
    }, [commodityCodeValue, runTableDataPresent]);

    const showSettings = () => {
        setDialogIsOpen(true);
    };

    const applySort = () => {
        const newCriteria = {
            propertyName: 'name',
            sortOrder: order,
        };
        if (runTableDataPresent) {
            let newSortCriteria = [];
            const index = sortCriteria.findIndex((elm: any) => elm['propertyName'] === 'name');
            if (index === -1) {
                newSortCriteria = [...sortCriteria, newCriteria];
            } else {
                newSortCriteria = [...sortCriteria.slice(0, index), { ...sortCriteria[index], sortOrder: order }, ...sortCriteria.slice(index + 1)];
            }
            setSortCriteria(newSortCriteria);
            setPage(1);
            setDoLoadMoreData(true);
            const headers = new AxiosHeaders();
            headers.set('CommodityCode', commodityCodeValue);
            axiosInstance
                .post(
                    `/commodity-codes-run/`,
                    {
                        filterCriteria: filterCriteria,
                        sortCriteria: newSortCriteria,
                    },
                    {
                        headers: headers,
                    }
                )
                .then((response: any) => response.data.content)
                .then(data => data)
                .then(response => {
                    setData(response);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    };

    const loadMoreData = async () => {
        if (doLoadMoreData) {
            setPage(prev => prev + 1);
            if (runTableDataPresent) {
                setLoading(true);
                const headers = new AxiosHeaders();
                headers.set('CommodityCode', commodityCodeValue);
                axiosInstance
                    .post(
                        `/commodity-codes-run/?page=${page}`,
                        {
                            filterCriteria: filterCriteria,
                            sortCriteria: sortCriteria,
                        },
                        {
                            headers: headers,
                        }
                    )
                    .then((response: any) => response.data)
                    .then(response => {
                        setDoLoadMoreData(!response.last);
                        setData((prev: any) => [...prev, ...response.content]);
                    })
                    .catch(error => {
                        setPage(prev => prev - 1);
                        console.log(error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    };

    const applyFilter = () => {
        console.log(filterParams);
        if (runTableDataPresent) {
            let newFilterCriteria = filterCriteria;
            filterParams.forEach((filterParam: any) => {
                if (filterParam.columnName.length > 0 && filterParam.freeText.length > 0) {
                    const newCriteria = {
                        column: filterParam.columnName,
                        value: filterParam.freeText,
                    };

                    const index = newFilterCriteria.findIndex((elm: any) => elm['column'] === filterParams.columnName);
                    if (index === -1) {
                        newFilterCriteria = [...newFilterCriteria, newCriteria];
                    } else {
                        newFilterCriteria = [
                            ...newFilterCriteria.slice(0, index),
                            { ...newFilterCriteria[index], value: filterParams.freeText },
                            ...newFilterCriteria.slice(index + 1),
                        ];
                    }
                }
            });
            setFilterCriteria(newFilterCriteria);
            setPage(1);
            setDoLoadMoreData(true);
            const headers = new AxiosHeaders();
            headers.set('CommodityCode', commodityCodeValue);
            axiosInstance
                .post(
                    `/commodity-codes-run/`,
                    {
                        filterCriteria: newFilterCriteria,
                        sortCriteria: sortCriteria,
                    },
                    {
                        headers: headers,
                    }
                )
                .then((response: any) => response.data.content)
                .then(data => data)
                .then(response => {
                    setData(response);
                })
                .catch(error => {
                    console.log(error);
                    setPage(0);
                });
        }
    };

    const resetFunctionality = () => {
        if (runTableDataPresent) {
            setFilterCriteria([
                {
                    column: 'runId',
                    value: productList.currentRunId,
                },
                {
                    column: 'deleted',
                    value: false,
                },
            ]);
            setSortCriteria([
                {
                    propertyName: 'enabled',
                    sortOrder: 'DESC',
                },
            ]);
            setPage(1);
            setDoLoadMoreData(true);
            const headers = new AxiosHeaders();
            headers.set('CommodityCode', commodityCodeValue);
            axiosInstance
                .post(
                    `/commodity-codes-run/`,
                    {
                        filterCriteria: [
                            {
                                column: 'runId',
                                value: productList.currentRunId,
                            },
                            {
                                column: 'deleted',
                                value: false,
                            },
                        ],
                        sortCriteria: [
                            {
                                propertyName: 'enabled',
                                sortOrder: 'DESC',
                            },
                        ],
                    },
                    {
                        headers: headers,
                    }
                )
                .then((response: any) => response.data.content)
                .then(data => data)
                .then(response => {
                    setData(response);
                })
                .catch(error => {
                    console.log(error);
                    setPage(0);
                });
        }
        filterComboxBoxRefList.current.forEach(ref => {
            if (ref && ref.fireEvent) {
                ref.value = '';
                ref.fireEvent('change');
                ref.fireEvent('input');
            }
        });

        filterInputRefList.current.forEach(ref => {
            if (ref && ref.fireEvent) {
                ref.value = '';
                ref.fireEvent('change');
                ref.fireEvent('input');
            }
        });
        setFilterColumns(content.tableData.filterableColumns);
    };

    const handleDownloadCSV = (response: any, name: string) => {
        const csvData = response.data;
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', name);
            link.setAttribute('rel', 'noopener noreferrer');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const downloadUploadedData = async () => {
        dispatch(loadingActions.startLoading());
        try {
            const response = await axiosInstance.get(`commodity-code/download/runId/${productList.currentRunId}`);
            handleDownloadCSV(response, 'SelectedCommodityCodes.csv');
        } catch (error) {
            console.log(error);
        } finally {
            dispatch(loadingActions.stopLoading());
        }
    };

    const handleMenuItemClick = async (event: Ui5CustomEvent<MenuDomRef, MenuItemClickEventDetail>) => {
        dispatch(loadingActions.startLoading());
        try {
            if (event.detail.item.id === 'CC_USER_DATA') {
                await downloadUploadedData();
            } else {
                const response = await axiosInstance.get(`commodity-code/download/sample/${event.detail.item.id}`);
                handleDownloadCSV(response, `SampleData${event.detail.item.text?.split(' ')[0]}.csv`);
            }
        } catch (e) {
            console.error(`Error Downloading ${event.detail.item.text}`);
        } finally {
            dispatch(loadingActions.stopLoading());
        }
        setUploadFlag(true);
    };

    const tableStructure = () => {
        return (
            <Card
                className="cardTable"
                header={
                    <Bar
                        style={{ boxShadow: 'none' }}
                        startContent={<CardHeader titleText={t(content.tableData.tableTitle)} />}
                        endContent={
                            <div style={{ display: 'flex', gap: '.25rem' }}>
                                <FileUploader ref={fileUploaderRef} hideInput accept=".csv" onChange={onFileChange} disabled={!uploadFlag}>
                                    <Button style={{ height: '1.5rem' }} tooltip={t('UPLOAD')} disabled={!uploadFlag}>
                                        {t('UPLOAD')}
                                    </Button>
                                </FileUploader>
                                <Button
                                    id={'openMenuBtn'}
                                    style={{ height: '1.5rem', border: 'none' }}
                                    design="Emphasized"
                                    onClick={() => {
                                        setMenuIsOpen(true);
                                    }}
                                    tooltip={t('SETTINGS_BUTTON')}
                                >
                                    {t('DOWNLOAD_SAMPLE_TEXT')}
                                </Button>

                                <Menu
                                    opener={'openMenuBtn'}
                                    open={menuIsOpen}
                                    onClose={() => {
                                        setMenuIsOpen(false);
                                    }}
                                    onItemClick={handleMenuItemClick}
                                >
                                    <MenuItem icon="download" text={t('UNSPSC_SAMPLE')} id="CC_MAIN_SAMPLE" />
                                    <MenuItem icon="download" text={t('CUSTOM_SAMPLE')} id="CC_SAMPLE" />
                                    {runTableDataPresent && <MenuItem icon="download" text={t('COMMODITY_CODE_USER_DOWNLOAD')} id="CC_USER_DATA" />}
                                </Menu>
                                <Button
                                    style={{ height: '1.5rem', width: '1.5rem', display: 'none' }}
                                    design="Emphasized"
                                    icon="action-settings"
                                    tooltip={t('SETTINGS_BUTTON')}
                                    disabled={commodityCodeValue !== 'unspsc' && !runTableDataPresent}
                                    onClick={showSettings}
                                ></Button>
                            </div>
                        }
                    />
                }
                style={{ width: '100%', overflowY: 'auto', borderRadius: 'none' }}
            >
                <Table
                    style={{ height: tableHeight, overflowY: 'auto', backgroundColor: 'eff1f2', padding: '0 1rem' }}
                    overflowMode="Scroll"
                    noDataText={t('UPLOAD_CSV_MESSAGE', (commodityCodeValue || '').toString())}
                    headerRow={
                        <TableHeaderRow sticky>
                            <TableHeaderCell width="8rem">
                                <Title level="H6">{t('DOMAIN')}</Title>
                            </TableHeaderCell>
                            <TableHeaderCell minWidth="200px">
                                <Title level="H6">{t('UNIQUE_ID')}</Title>
                            </TableHeaderCell>
                            <TableHeaderCell minWidth="200px">
                                <Title level="H6">{t('NAME')}</Title>
                            </TableHeaderCell>
                            <TableHeaderCell width="100px">
                                <Title level="H6">{t('ENABLED')}</Title>
                            </TableHeaderCell>
                            <TableHeaderCell minWidth="200px">
                                <Title level="H6">{t('PARENT_ID')}</Title>
                            </TableHeaderCell>
                        </TableHeaderRow>
                    }
                    features={<TableGrowing onLoadMore={loadMoreData} type="Scroll" />}
                    loading={loading}
                    loadingDelay={0}
                >
                    {data.map((item: any) => {
                        return (
                            <>
                                <TableRow>
                                    <TableCell style={{ paddingLeft: '1rem' }}>
                                        <Label>{item.domain}</Label>
                                    </TableCell>
                                    <TableCell style={{ paddingLeft: '0.5rem' }}>
                                        <Label>{item.uniqueKey}</Label>
                                    </TableCell>
                                    <TableCell style={{ paddingLeft: '0.5rem' }}>
                                        <Label>{item.name}</Label>
                                    </TableCell>
                                    <TableCell style={{ paddingLeft: '0.5rem' }}>
                                        <Label>{item.enabled ? 'Yes' : 'No'}</Label>
                                    </TableCell>
                                    <TableCell style={{ paddingLeft: '0.5rem' }}>
                                        <Label>{item.parentUniqueKey}</Label>
                                    </TableCell>
                                </TableRow>
                            </>
                        );
                    })}
                </Table>
            </Card>
        );
    };

    useEffect(() => {
        setFilterColumns(content.tableData.filterableColumns);
        const rowCount = content.tableData.filterableColumns.length;
        if (filterParams.length === 0) {
            for (let i = 0; i < rowCount; i++) {
                setFilterParams((prev: any) => [...prev, { columnName: '', freeText: '' }]);
                setFilterParamsForUnspsc((prev: any) => [...prev, { columnName: '', freeText: '' }]);
            }
        }
    }, [content && content.tableData && content.tableData.filterableColumns]);

    return (
        <FlexBox style={styles.wrapper} justifyContent="Center">
            <FlexBox style={{ gap: 32 }} direction="Column" alignItems="Center">
                <FlexBox direction="Column" style={styles.headerWrapper}>
                    <Title level="H4">{t('ENABLEMENT')}</Title>
                </FlexBox>

                <FlexBox wrap="Wrap" style={styles.cardsWrapper} justifyContent="Center">
                    <>
                        {tableStructure()}

                        {content && content.tableData && (
                            <Dialog
                                className="settings-dialog"
                                footer={
                                    <Bar
                                        design="Footer"
                                        endContent={
                                            <div style={{ display: 'flex', gap: '.25rem' }}>
                                                <Button onClick={resetFunctionality}>{t('RESET_BUTTON')}</Button>
                                                <Button
                                                    onClick={() => {
                                                        setDialogIsOpen(false);
                                                    }}
                                                >
                                                    {t('CLOSE_BUTTON')}
                                                </Button>
                                            </div>
                                        }
                                    />
                                }
                                headerText={t('VIEW_SETTINGS')}
                                open={dialogIsOpen}
                                style={{ width: '600px', height: '400px', zIndex: 1000 }}
                            >
                                <TabContainer>
                                    <Tab text="Sort" selected>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text> By Name</Text>
                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                {['ASC', 'DESC'].map((elm: any) => {
                                                    return (
                                                        <RadioButton
                                                            name="Name"
                                                            key={elm}
                                                            text={elm}
                                                            onChange={() => {
                                                                setOrder(elm);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>

                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                <Button onClick={applySort}>{t('APPLY')}</Button>
                                            </div>
                                        </div>
                                    </Tab>
                                    <Tab text="Filter">
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: '0.25rem',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {content?.tableData?.filterableColumns?.map((column: any, index: number) => {
                                                return (
                                                    <div style={{ display: 'flex', gap: '0.25rem' }} key={column}>
                                                        <ComboBox
                                                            ref={el => (filterComboxBoxRefList.current[index] = el)}
                                                            key={column}
                                                            onSelectionChange={(e: any) => {
                                                                const selectedColumnDataIndex = content?.tableData?.tableColumn?.filter(
                                                                    (column: any) => column.title === e.target.value
                                                                )[0].dataIndex;
                                                                if (commodityCodeValue === 'unspsc' && !runTableDataPresent) {
                                                                    setFilterParamsForUnspsc((prevPrams: any) => {
                                                                        return [
                                                                            ...prevPrams.slice(0, index),
                                                                            { ...prevPrams[index], columnName: selectedColumnDataIndex },
                                                                            ...prevPrams.slice(index + 1),
                                                                        ];
                                                                    });
                                                                } else if (runTableDataPresent) {
                                                                    setFilterParams((prevPrams: any) => {
                                                                        return [
                                                                            ...prevPrams.slice(0, index),
                                                                            { ...prevPrams[index], columnName: selectedColumnDataIndex },
                                                                            ...prevPrams.slice(index + 1),
                                                                        ];
                                                                    });
                                                                }
                                                                setFilterColumns((prev: any) => prev.filter((elm: any) => elm !== e.target.value));
                                                                e.currentTarget?.blur();
                                                            }}
                                                            placeholder={t('FILTER_BY')}
                                                            showClearIcon={true}
                                                            onInput={(event: any) => {
                                                                if (
                                                                    filterColumns.indexOf(lastSelectedItemForFilter) === -1 &&
                                                                    lastSelectedItemForFilter !== ''
                                                                ) {
                                                                    setFilterColumns((prevValue: any) => [...prevValue, lastSelectedItemForFilter]);
                                                                }
                                                            }}
                                                            onFocus={(event: any) => {
                                                                if (event.target.value !== '') {
                                                                    setLastSelectedItemForFilter(event.target.value);
                                                                }
                                                            }}
                                                        >
                                                            {filterColumns.map((column: any) => {
                                                                return <ComboBoxItem key={column} text={column} />;
                                                            })}
                                                        </ComboBox>
                                                        <Input
                                                            ref={el => (filterInputRefList.current[index] = el)}
                                                            key={column}
                                                            placeholder={t('ENTER_SOME_VALUE')}
                                                            onChange={e => {
                                                                if (commodityCodeValue === 'unspsc' && !runTableDataPresent) {
                                                                    setFilterParamsForUnspsc((prevPrams: any) => {
                                                                        return [
                                                                            ...prevPrams.slice(0, index),
                                                                            { ...prevPrams[index], freeText: e.target.value },
                                                                            ...prevPrams.slice(index + 1),
                                                                        ];
                                                                    });
                                                                } else if (runTableDataPresent) {
                                                                    setFilterParams((prevPrams: any) => {
                                                                        return [
                                                                            ...prevPrams.slice(0, index),
                                                                            { ...prevPrams[index], freeText: e.target.value },
                                                                            ...prevPrams.slice(index + 1),
                                                                        ];
                                                                    });
                                                                }
                                                            }}
                                                            showClearIcon={true}
                                                        ></Input>
                                                    </div>
                                                );
                                            })}
                                            <Button onClick={applyFilter}>{t('APPLY')}</Button>
                                        </div>
                                    </Tab>
                                </TabContainer>
                            </Dialog>
                        )}

                        <MessageBox
                            type={MessageBoxType.Error}
                            open={openErrorDialog}
                            onClose={() => setOpenErrorDialog(false)}
                            data-testid="err-dialog"
                        >
                            <Text>{t(errorMessage)}</Text>
                        </MessageBox>
                    </>
                </FlexBox>
            </FlexBox>
        </FlexBox>
    );
};

export default TableLayout;

const styles = createStyles({
    wrapper: {
        padding: '40px 16px 0 16px',
        background: 'white',
        borderRadius: ThemingParameters.sapElement_BorderCornerRadius,
        maxHeight: '100%',
        overflowY: 'auto',
        boxShadow: ThemingParameters.sapContent_Shadow0,
    },

    headerWrapper: {
        gap: 8,
        textAlign: 'center',
    },

    cardsWrapper: {
        gap: 16,
        paddingBottom: 32,
    },
});
